/**
 * What the IRS accepts in each kind of column, checked on every column of every form.
 *
 * checkRecords used to look at the handful of columns it knew by name: the two TINs, the
 * recipient's state, city and ZIP. Everything else went through untouched, and the send-side
 * sanitizers quietly "fixed" it - a TIN with letters became 000000000, a foreign postal code
 * was never looked at, a phone number with words in it lost the words. A customer could save
 * "gfdsagfeatgagffd" as a ZIP code and only find out when the IRS rejected the transmission.
 *
 * This pass reads the generated field rules (what core-api says each column is) and, where a
 * column is only called "text", its name - FILER_PHONE, STUDENT_ZIP, BOX_1B_DATE_ACQUIRED say
 * what they hold - and applies the rule the IRS schema applies to the element the column ends
 * up in (IRS-IRefileTypes.xsd): TINType is nine digits, ZIPCodeType is 5, 9 or 12 digits,
 * ForeignPostalCd is at most 16 characters, DateType is a calendar date, EmailAddressType has an
 * @ in it, ContactPhoneNumberType is digits, CityType is letters, StateType is the state list.
 *
 * Blank is never wrong here. Whether a column may be blank is decided elsewhere (the required
 * columns in checkRecords and the zod shape); this pass only says whether what was written can
 * be filed.
 */
import type { FieldRule } from "./fieldRules";
import { irsCountryCode } from "./countryCodes.generated";
import { US_STATE_CODES, ZIP_RE } from "./recordValidation";

export type FieldClass =
  | "tin"
  | "zip"
  | "state"
  | "date"
  | "phone"
  | "email"
  | "country"
  | "city"
  | "address"
  | "name"
  | "idType";

export interface FieldIssue {
  field: string;
  code: string;
  message: string;
}

const FIELD_CHECK_CODES = {
  INVALID_TIN: "INVALID_TIN",
  INVALID_ZIP: "INVALID_ZIP",
  INVALID_POSTAL_CODE: "INVALID_POSTAL_CODE",
  INVALID_STATE: "INVALID_STATE",
  INVALID_DATE: "INVALID_DATE",
  INVALID_PHONE: "INVALID_PHONE",
  INVALID_EMAIL: "INVALID_EMAIL",
  UNKNOWN_COUNTRY: "UNKNOWN_COUNTRY",
  INVALID_TEXT: "INVALID_TEXT",
  INVALID_ID_TYPE: "INVALID_ID_TYPE",
} as const;

export { FIELD_CHECK_CODES };

/** The spellings of "this address is in the United States" a country column may carry. */
const UNITED_STATES = /^(US|USA|U\.?S\.?A?\.?|UNITED STATES( OF AMERICA)?)$/i;

const digits = (v: unknown) => String(v ?? "").replace(/\D/g, "");

/** A TIN the IRS will reject on sight: wrong length, or an obvious placeholder. */
export function tinLooksInvalid(raw: string): boolean {
  const d = digits(raw);
  if (d.length !== 9) return true;
  if (/^(\d)\1{8}$/.test(d)) return true; // 000000000, 111111111, ...
  return d === "123456789";
}

/**
 * Which rule a column gets. The generated kind wins when it says something; a column the
 * rules only call "text" is read by its name, because the same column is "zip" in one year's
 * definition and "text" in another's (EMPLOYEE_ZIP, RECIPIENT_STATE), and a ZIP does not
 * stop being a ZIP because a definition forgot to say so.
 */
export function classifyField(name: string, rule: FieldRule | undefined): FieldClass | null {
  const kind = rule?.kind;
  if (kind === "amount" || kind === "checkbox") return null; // the zod shape covers these
  if (kind === "tin") return "tin";
  if (kind === "zip") return "zip";
  if (kind === "state") return "state";
  if (kind === "date") return "date";
  if (kind === "idType") return "idType";

  const n = name.toUpperCase();
  if (/ID_TYPE$/.test(n)) return "idType";
  if (/(^|_)(ZIP|ZIP_CODE|POSTAL_CODE)$/.test(n)) return "zip";
  // Flags and IRS codes that merely mention one of the words below.
  if (/(_IND|INDICATOR|_CODE|_CD|_TYPE|SOLICITED|NOTICE)$/.test(n)) return null;

  if (/(PHONE|TELEPHONE)/.test(n)) return "phone";
  if (/EMAIL/.test(n)) return "email";
  // A US TIN by any of its names. A foreign TIN is whatever that country issues.
  if (/FOREIGN_TIN/.test(n)) return null;
  if (/(^|_)(SSN|TIN|EIN|ID_NUMBER|FEDERAL_ID_NUMBER|PAYERS_TIN)$/.test(n)) return "tin";
  if (/(^|_)COUNTRY$/.test(n)) return "country";
  // The state a US address is in, and the state whose tax a state box reports. Not a state
  // identification number, a state's name written out, or a state amount.
  if (/(^|_)STATE(_NAME)?[12]?$/.test(n) && !/NAME_OF_STATE|STATE_(ID|NO|NUMBER|TAX|INCOME|DISTRIBUTION)/.test(n)) {
    return "state";
  }
  // Only dates the IRS is told about: a box date. CREATED_DATE and its kin are bookkeeping.
  if (/^BOX/.test(n) && /(^|_)DATE(_|$)/.test(n)) return "date";
  if (/(^|_)CITY$/.test(n)) return "city";
  if (/(^|_)ADDRESS[0-9]?$/.test(n)) return "address";
  if (/NAME[0-9]?$/.test(n) && !/NAME_OF/.test(n)) return "name";
  return null;
}

/**
 * The prefix that names whose column this is: FILER in FILER_ZIP, BOX13D_RECIPIENT in
 * BOX13D_RECIPIENT_STATE. That prefix is how the address's country column is found.
 */
function entityOf(name: string): string {
  return name.replace(/_(ZIP|ZIP_CODE|POSTAL_CODE|STATE|CITY|COUNTRY|ADDRESS[0-9]?|PHONE|EMAIL)$/i, "");
}

/**
 * Is the address this column belongs to outside the United States? The address's own
 * country column decides (FILER_COUNTRY for FILER_ZIP), then its foreign indicator. The
 * recipient's address also follows the record-level answer the caller already worked out
 * from the form's identity columns.
 */
function addressIsForeign(
  record: Record<string, unknown>,
  name: string,
  recipientEntity: string,
  recipientForeign: boolean
): boolean {
  const entity = entityOf(name);
  const country = String(record[`${entity}_COUNTRY`] ?? "").trim();
  if (country) return !UNITED_STATES.test(country) && irsCountryCode(country) !== "US";
  const indicator = String(record[`${entity}_FOREIGN_INDICATOR`] ?? "").trim();
  if (indicator && indicator !== "0") return true;
  return entity === recipientEntity && recipientForeign;
}

/**
 * The day a date column names, as YYYY-MM-DD (sortable), or null when it is not a real
 * calendar date. Accepts what a spreadsheet writes as well as ISO.
 */
export function calendarDayOf(raw: string): string | null {
  const s = raw.trim();
  let y: number, m: number, d: number;
  let match: RegExpMatchArray | null;
  if ((match = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ].*)?$/))) {
    [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  } else if ((match = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2}|\d{4})$/))) {
    [m, d] = [Number(match[1]), Number(match[2])];
    y = match[3].length === 2 ? 2000 + Number(match[3]) : Number(match[3]);
  } else if ((match = s.match(/^(\d{4})(\d{2})(\d{2})$/))) {
    [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  } else {
    return null;
  }
  if (y < 1900 || y > 2100 || m < 1 || m > 12 || d < 1) return null;
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  if (d > daysInMonth) return null;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** DateType: a real calendar date. Accepts what a spreadsheet writes as well as ISO. */
export function parseCalendarDate(raw: string): boolean {
  return calendarDayOf(raw) !== null;
}

/**
 * Check every column of one record against the rule for its kind.
 *
 * `recipientForeign` is the record-level answer checkRecords already has (the form's own
 * country column or foreign indicator); `recipientZipField` names the recipient's ZIP column
 * so the other address columns of the same entity follow it.
 */
export function checkFieldValues(
  record: Record<string, unknown>,
  rules: Record<string, FieldRule>,
  options: { recipientForeign: boolean; recipientZipField: string }
): FieldIssue[] {
  const issues: FieldIssue[] = [];
  const recipientEntity = entityOf(options.recipientZipField);
  const labelOf = (field: string) => rules[field]?.label ?? field;

  for (const [field, raw] of Object.entries(record)) {
    if (raw === undefined || raw === null) continue;
    const value = String(raw).trim();
    if (value === "") continue;
    const cls = classifyField(field, rules[field]);
    if (!cls) continue;
    const label = labelOf(field);

    switch (cls) {
      case "tin": {
        if (tinLooksInvalid(value)) {
          issues.push({
            field,
            code: FIELD_CHECK_CODES.INVALID_TIN,
            message: `${label} "${value}" is not a valid 9-digit TIN.`,
          });
        }
        break;
      }
      case "zip": {
        if (addressIsForeign(record, field, recipientEntity, options.recipientForeign)) {
          // ForeignPostalCd: at most 16 characters. Postal codes are letters, digits, spaces
          // and hyphens, and every postal system in use has at least one digit in its codes.
          const ok = value.length <= 16 && /^[A-Za-z0-9 -]+$/.test(value) && /\d/.test(value);
          if (!ok) {
            issues.push({
              field,
              code: FIELD_CHECK_CODES.INVALID_POSTAL_CODE,
              message:
                `${label} "${value}" is not a postal code. Use the letters, digits, spaces ` +
                "and hyphens of the postal code as that country writes it, up to 16 characters.",
            });
          }
        } else {
          // ZIPCodeType: 5 digits, or 9, or 12. A hyphen or space between the parts is fine.
          const compact = value.replace(/[\s-]/g, "");
          if (!/^\d+$/.test(compact) || !ZIP_RE.test(compact)) {
            issues.push({
              field,
              code: FIELD_CHECK_CODES.INVALID_ZIP,
              message: `${label} "${value}" is not a valid US ZIP code. Use 5, 9 or 12 digits.`,
            });
          }
        }
        break;
      }
      case "state": {
        if (addressIsForeign(record, field, recipientEntity, options.recipientForeign)) break;
        if (!US_STATE_CODES.has(value.toUpperCase())) {
          issues.push({
            field,
            code: FIELD_CHECK_CODES.INVALID_STATE,
            message: `${label} "${value}" is not a US state, territory or military code.`,
          });
        }
        break;
      }
      case "date": {
        // Form 1099-B lets a broker write "Various" for the date acquired.
        if (/ACQUIRED/i.test(field) && /^various$/i.test(value)) break;
        if (!parseCalendarDate(value)) {
          issues.push({
            field,
            code: FIELD_CHECK_CODES.INVALID_DATE,
            message: `${label} "${value}" is not a date. Write it as MM/DD/YYYY or YYYY-MM-DD.`,
          });
        }
        break;
      }
      case "phone": {
        // ContactPhoneNumberType: digits, up to 30. A US number has 10; an extension may follow.
        const d = digits(value);
        const ok = /^[\d\s()+\-.xX,]*(ext\.?|extension)?[\d\s()+\-.,]*$/i.test(value) && d.length >= 10 && d.length <= 30;
        if (!ok) {
          issues.push({
            field,
            code: FIELD_CHECK_CODES.INVALID_PHONE,
            message: `${label} "${value}" is not a phone number. Use the 10-digit number, with an extension after it if there is one.`,
          });
        }
        break;
      }
      case "email": {
        // EmailAddressType: something, an @, something, at most 75 characters.
        if (value.length > 75 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          issues.push({
            field,
            code: FIELD_CHECK_CODES.INVALID_EMAIL,
            message: `${label} "${value}" is not an email address.`,
          });
        }
        break;
      }
      case "country": {
        if (UNITED_STATES.test(value)) break;
        if (!irsCountryCode(value)) {
          issues.push({
            field,
            code: FIELD_CHECK_CODES.UNKNOWN_COUNTRY,
            message:
              `The IRS does not recognise "${value}" as a country (${label}). ` +
              "Use the country's full name as the IRS spells it, or its two-letter IRS code.",
          });
        }
        break;
      }
      case "city": {
        // CityType: letters and spaces, up to 40 (50 abroad). Punctuation is dropped on the
        // way out, so only a city with no letters, or with digits, is refused here.
        if (!/[A-Za-z]/.test(value) || /\d/.test(value) || value.length > 50) {
          issues.push({
            field,
            code: FIELD_CHECK_CODES.INVALID_TEXT,
            message: `${label} "${value}" is not a city name. Use letters only, up to 40 characters.`,
          });
        }
        break;
      }
      case "address": {
        // StreetAddressType: letters, digits, space, hyphen and slash, up to 35.
        if (!/[A-Za-z0-9]/.test(value) || value.length > 35) {
          issues.push({
            field,
            code: FIELD_CHECK_CODES.INVALID_TEXT,
            message: `${label} "${value}" is not an address line. Use up to 35 letters and digits.`,
          });
        }
        break;
      }
      case "name": {
        // PersonNameType is 35 characters, BusinessNameLine1Type is 75; both need a letter.
        const limit = /BUSINESS/i.test(field) ? 75 : /(FIRST|MIDDLE|LAST)_NAME/i.test(field) ? 35 : 75;
        if (!/[A-Za-z]/.test(value) || value.length > limit) {
          issues.push({
            field,
            code: FIELD_CHECK_CODES.INVALID_TEXT,
            message: `${label} "${value}" is not a name. Use up to ${limit} characters, with at least one letter.`,
          });
        }
        break;
      }
      case "idType": {
        // '1' = EIN, '2' = SSN/ITIN/ATIN, '3' = unknown; the spelled-out forms are read too.
        if (!/^(1|2|3|E|S|EIN|SSN|ITIN|ATIN|UNKNOWN|BUSINESS|INDIVIDUAL)$/i.test(value)) {
          issues.push({
            field,
            code: FIELD_CHECK_CODES.INVALID_ID_TYPE,
            message: `${label} "${value}" is not a TIN type. Use 1 for an EIN, 2 for an SSN, ITIN or ATIN.`,
          });
        }
        break;
      }
    }
  }
  return issues;
}
